import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import cytoscape, {Core} from 'cytoscape';
import {EdgeTypeMapCy, GraphMap, GraphNode} from '../../../api/interfaces/graph-node.interface';
import {DefaultValues, strokeDashPattern, strokeLineCap} from '@maporium-workspace/shared';
import {transformColorWithHSL} from '../../helpers/color-helpers';
import {cytoscapeStrokeStyle} from '../../helpers/cytoscape-dash.helper';
import {SettingsService} from '../../../api/services/setting.service';
import {environment} from '../../../../environments/environment';
import {cyNodeShape} from '../../helpers/node-shape.helper';

/** Rendered border width of a preview node, in px (MAP-346: shared with the dash pattern). */
function previewNodeBorderWidth(node: any): number {
  const weight = node.data('weight');
  return weight != null ? weight * DefaultValues.weightMultiplier : 0;
}

/** Rendered line width of a preview link, in px. */
function previewLinkLineWidth(edge: any): number {
  const weight = edge.data('weight') as number;
  if (weight && weight >= 1) return weight;
  if (weight && weight < 1) return weight === 0 ? 0.25 : 0.5;
  return 0.1;
}

/** Preview links carry the shape in either case (AI payloads use upper case). */
function previewLinkStyle(edge: any): string {
  const shape = String(edge.data('shape') ?? '').toLowerCase();
  return shape === 'dashed' || shape === 'dotted' ? shape : 'solid';
}

@Component({
  selector: 'cy-preview',
  standalone: true,
  template: `
    <div class="cy-preview-wrapper">
      <div class="background-overlay"></div>
      <div class="background-image"></div>
      <div #cyContainer class="cy-preview-container" [style.visibility]="snapshotMode ? 'hidden' : 'visible'"></div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .cy-preview-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    .background-overlay,
    .background-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }
    .background-overlay {
      z-index: 0;
    }
    .background-image {
      z-index: 1;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    .cy-preview-container {
      position: relative;
      width: 100%;
      height: 100%;
      z-index: 2;
    }
  `]
})
export class CyPreviewComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() nodes: GraphNode[] = [];
  @Input() mapStyles?: GraphMap['styles'];
  @Input() disableSnapshot = false;

  @ViewChild('cyContainer', { static: true }) cyContainer!: ElementRef;

  private cy: Core | null = null;
  private hostElement: ElementRef;
  public snapshotMode = false;
  private isSnapshotted = false;

  constructor(
    private settingsService: SettingsService,
    private elementRef: ElementRef
  ) {
    this.hostElement = elementRef;
  }

  ngOnInit(): void {
    // Use disableSnapshot input to override settings, otherwise read from settings
    this.snapshotMode = this.disableSnapshot
      ? false
      : (this.settingsService.getLocalStorageSettings().snapshotPreviewsEnabled ?? true);
  }

  ngAfterViewInit(): void {
    this.applyBackgroundStyles();
    this.initCytoscape();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapStyles']) {
      this.applyBackgroundStyles();
    }
    if ((changes['nodes'] || changes['mapStyles']) && this.cy) {
      this.updateElements();
    }
  }

  ngOnDestroy(): void {
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
  }

  private isDarkMode(): boolean {
    return this.settingsService.isDarkMode(this.mapStyles?.appearance);
  }

  private applyBackgroundStyles(): void {
    const wrapper = this.hostElement.nativeElement.querySelector('.cy-preview-wrapper');
    if (!wrapper) return;

    const overlayEl = wrapper.querySelector('.background-overlay') as HTMLElement;
    const imageEl = wrapper.querySelector('.background-image') as HTMLElement;

    if (!overlayEl || !imageEl) return;

    const styles = this.mapStyles;
    const isDark = this.isDarkMode();

    // Apply overlay color
    if (styles?.color) {
      const intensity = styles.colorIntensity ?? 1;
      let overlayColor: string;

      if (intensity === 0) {
        overlayColor = isDark ? '#000000' : '#FFFFFF';
      } else {
        const targetLuminance = isDark ? 0.0 : 1.0;
        overlayColor = transformColorWithHSL(styles.color, targetLuminance, intensity);
      }
      overlayEl.style.backgroundColor = overlayColor;
    } else {
      overlayEl.style.backgroundColor = isDark ? '#000000' : '#FFFFFF';
    }

    // Apply background image
    if (styles?.backgroundImageUrl) {
      imageEl.style.backgroundImage = `url(${styles.backgroundImageUrl})`;
      imageEl.style.opacity = (styles.opacity ?? 1).toString();
    } else {
      imageEl.style.backgroundImage = 'none';
    }
  }

  private initCytoscape(): void {
    const elements = this.buildElements();

    this.cy = cytoscape({
      container: this.cyContainer.nativeElement,
      elements,
      style: this.buildStyles(),
      layout: { name: 'preset' },

      // Completely static - no interaction
      autolock: true,
      autoungrabify: true,
      autounselectify: true,
      userPanningEnabled: false,
      userZoomingEnabled: false,
      boxSelectionEnabled: false,
      selectionType: 'single',

      // Performance
      textureOnViewport: true,
      hideEdgesOnViewport: false,
      hideLabelsOnViewport: false,

      minZoom: 0.01,
      maxZoom: 10
    });

    // Initialize edge editing for bend/control points (read-only)
    this.initEdgeEditing();

    // Fit to container after initialization
    setTimeout(() => {
      this.cy?.fit(undefined, 10);
      this.cy?.center();

      // If snapshot mode is enabled, capture and destroy after render
      if (this.snapshotMode) {
        // Use requestAnimationFrame to ensure paint has completed
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.captureAndReplace();
          });
        });
      }
    }, 50);
  }

  /**
   * Capture the cytoscape canvas as an image, replace the container contents,
   * and destroy the cytoscape instance to free memory.
   */
  private captureAndReplace(retryCount = 0): void {
    if (!this.cy || this.isSnapshotted) return;

    // Check if there are elements to render
    const elements = this.cy.elements();

    if (elements.length === 0) {
      // No elements, just destroy
      this.cy.destroy();
      this.cy = null;
      this.isSnapshotted = true;
      return;
    }

    // Check if elements have rendered (have valid bounding box)
    const bb = elements.boundingBox();

    if ((bb.w === 0 || bb.h === 0) && retryCount < 5) {
      // Not rendered yet, retry after delay
      setTimeout(() => this.captureAndReplace(retryCount + 1), 100);
      return;
    }

    try {
      // Export as PNG data URL - capture all elements
      const dataUrl = this.cy.png({
        output: 'base64uri',
        bg: 'transparent',
        full: true, // Capture all elements, not just viewport
        maxWidth: 800,
        maxHeight: 450
      });

      if (!dataUrl || dataUrl.length < 100) {
        console.warn('[Snapshot] Invalid dataUrl, keeping cy instance');
        return;
      }

      // Create image element
      const img = document.createElement('img');
      img.src = dataUrl;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      img.style.position = 'absolute';
      img.style.top = '0';
      img.style.left = '0';
      img.style.zIndex = '10';
      img.className = 'cy-snapshot-img';

      // Get the wrapper (parent of cy container) and append image there
      const wrapper = this.hostElement.nativeElement.querySelector('.cy-preview-wrapper');
      if (wrapper) {
        wrapper.appendChild(img);
      }

      // Hide the cy container (don't remove - Angular might recreate)
      const container = this.cyContainer.nativeElement;
      container.style.display = 'none';

      // Destroy cytoscape instance to free memory
      this.cy.destroy();
      this.cy = null;
      this.isSnapshotted = true;
    } catch (e) {
      // If snapshot fails, keep the live cytoscape instance
      console.warn('Failed to capture preview snapshot:', e);
    }
  }

  private initEdgeEditing(): void {
    if (!this.cy) return;

    // Check if edge editing extension is available
    if (!(this.cy as any).edgeEditing) return;

    // Initialize edge editing in a minimal/disabled mode for rendering only
    const edgeEditingInstance = (this.cy as any).edgeEditing({
      undoable: false,
      bendRemovalSensitivity: 0,
      enableMultipleAnchorRemovalOption: false,
      initAnchorsAutomatically: false,
      enableCreateAnchorOnDrag: false,
      addBendMenuItemTitle: false,
      removeBendMenuItemTitle: false,
      removeAllBendMenuItemTitle: false,
      handleReconnectEdge: false,
      enableDisplayAnchorShapeAndSize: false,
      zIndex: 0
    });

    // Initialize anchor points for edges that have bend/control point data
    const edges = this.cy.edges();
    edges.forEach((edge: any) => {
      const type = edge.data('type');
      if (type && type !== 'BEZIER') {
        const hasBendPoints = edge.data('cyedgebendeditingDistances')?.length > 0 ||
                              edge.data('bendPointPositions')?.length > 0;
        const hasControlPoints = edge.data('cyedgecontroleditingDistances')?.length > 0 ||
                                 edge.data('controlPointPositions')?.length > 0;

        if (hasBendPoints || hasControlPoints) {
          try {
            edgeEditingInstance.initAnchorPoints([edge]);
          } catch (e) {
            // Silently ignore errors in preview mode
          }
        }
      }
    });
  }

  private updateElements(): void {
    // If already snapshotted, need to re-initialize
    if (this.isSnapshotted) {
      this.isSnapshotted = false;
      this.initCytoscape();
      return;
    }

    if (!this.cy) return;

    this.cy.elements().remove();
    this.cy.add(this.buildElements());
    this.cy.style(this.buildStyles());
    this.cy.fit(undefined, 10);
    this.cy.center();

    // Re-capture if in snapshot mode
    if (this.snapshotMode) {
      setTimeout(() => {
        this.captureAndReplace();
      }, 150);
    }
  }

  private buildElements(): cytoscape.ElementDefinition[] {
    const elements: cytoscape.ElementDefinition[] = [];
    const nodeIds = new Set<string>();

    const processNode = (node: GraphNode, parentId?: string) => {
      if (!node.id) return;

      nodeIds.add(node.id);

      elements.push({
        group: 'nodes',
        data: {
          id: node.id,
          parent: parentId,
          name: node.name,
          color: node.color || DefaultValues.node.color,
          type: node.type || DefaultValues.node.type,
          weight: node.weight,
          opacity: node.properties?.opacity,
          colorOpacity: node.properties?.colorOpacity,
          scale: node.properties?.scale,
          borderStyle: node.properties?.borderStyle,
          label: node.properties?.label || 'name',
          textFontSize: node.properties?.textFontSize,
          textValign: node.properties?.textValign,
          textHalign: node.properties?.textHalign,
          textMarginX: node.properties?.textMarginX,
          textMarginY: node.properties?.textMarginY,
          imageUrl: node.imageUrl,
          imageOpacity: node.properties?.imageOpacity
        },
        position: { x: node.x ?? 0, y: node.y ?? 0 }
      });

      if (node.children?.length) {
        const nodeId = node.id;
        node.children.forEach(child => processNode(child, nodeId));
      }

      if (node.links?.length) {
        node.links.forEach(link => {
          const fromId = link.from?.id || (link as any).fromId;
          const toId = link.to?.id || (link as any).toId;

          elements.push({
            group: 'edges',
            data: {
              id: link.id,
              source: fromId,
              target: toId,
              name: link.name,
              color: link.color || DefaultValues.link.color,
              type: link.type || DefaultValues.link.type,
              weight: link.weight,
              shape: link.shape || DefaultValues.link.shape,
              direction: link.direction || DefaultValues.link.direction,
              edgeArrowType: link.edgeArrowType || DefaultValues.link.edgeArrowType,
              label: link.label || 'name',
              textFontSize: link.textFontSize,
              linkLabelOffset: link.linkLabelOffset,
              labelSlope: link.labelSlope,
              // Control/bend point data for segmented and curved edges
              cyedgebendeditingWeights: link.cyedgebendeditingWeights,
              cyedgebendeditingDistances: link.cyedgebendeditingDistances,
              cyedgecontroleditingWeights: link.cyedgecontroleditingWeights,
              cyedgecontroleditingDistances: link.cyedgecontroleditingDistances,
              bendPointPositions: link.bendPointPositions,
              controlPointPositions: link.controlPointPositions
            }
          });
        });
      }
    };

    this.nodes.forEach(node => processNode(node));

    return elements.filter(el => {
      if (el.group === 'edges') {
        return nodeIds.has(el.data.source) && nodeIds.has(el.data.target);
      }
      return true;
    });
  }

  private buildStyles(): any[] {
    const isDark = this.isDarkMode();
    const targetLuminance = isDark ? 0.0 : 1.0;
    const labelColor = isDark ? '#FFFFFF' : '#000000';

    return [
      {
        selector: 'node',
        style: {
          'background-color': (node: any) => {
            const color = node.data('color') || DefaultValues.node.color;
            const nodeOpacity = node.data('opacity');

            if (nodeOpacity === 0 || nodeOpacity === '0') {
              return isDark ? '#000' : '#fff';
            }

            const trueOpacity = nodeOpacity ?? DefaultValues.node.properties.opacity;
            return transformColorWithHSL(color, targetLuminance, trueOpacity);
          },
          'shape': (node: any) => cyNodeShape(node.data('type')),
          'width': (node: any) => {
            const scale = node.data('scale');
            const effectiveScale = (scale === '0' || scale === 0) ? 0.25 : (scale ?? 1);
            return effectiveScale * 10;
          },
          'height': (node: any) => {
            const scale = node.data('scale');
            const effectiveScale = (scale === '0' || scale === 0) ? 0.25 : (scale ?? 1);
            return effectiveScale * 10;
          },
          'background-opacity': (node: any) => {
            const colorOpacity = node.data('colorOpacity') ?? DefaultValues.node.properties.colorOpacity;
            return colorOpacity > 1 ? 1 : colorOpacity;
          },
          'background-image': (node: any) => {
            const imageUrl = node.data('imageUrl');
            return imageUrl
                ? environment.apiUrl + '/public/image?url=' + encodeURIComponent(imageUrl)
                : 'none';
          },
          'background-image-opacity': (node: any) => {
            const imageOpacity = node.data('imageOpacity');
            const opacity = imageOpacity ?? DefaultValues.node.properties.imageOpacity ?? 1;
            return opacity > 1 ? 1 : opacity;
          },
          'background-fit': 'cover',
          // MAP-346: 'dotted' rides the 'dashed' branch (see cytoscape-dash.helper).
          'border-style': (node: any) => cytoscapeStrokeStyle(node.data('borderStyle')),
          // MAP-346: same width-scaled dash geometry as the real canvas, so the
          // preview cannot disagree with what it is previewing.
          //@ts-ignore
          'border-dash-pattern': (node: any) =>
            strokeDashPattern(node.data('borderStyle'), previewNodeBorderWidth(node)) ?? [4, 2],
          //@ts-ignore
          'border-cap': (node: any) => strokeLineCap(node.data('borderStyle')),
          //@ts-ignore
          'border-join': 'round',
          'border-width': (node: any) => `${previewNodeBorderWidth(node)}px`,
          'border-color': (node: any) => node.data('color') || DefaultValues.node.color,
          'z-index': 3,
          'z-index-compare': 'manual'
        }
      },
      // Node labels - only for nodes with label property defined
      {
        selector: 'node[label]',
        style: {
          'label': (node: any) => {
            const label = node.data('label');
            if (label === 'name') {
              return node.data('name') || '';
            }
            // For preview, we don't load metadata, so just use name as fallback
            return node.data('name') || '';
          },
          'font-family': 'Roboto, sans-serif',
          'font-size': (node: any) => node.data('textFontSize') ?? DefaultValues.node.properties.textFontSize,
          'color': labelColor,
          'text-valign': (node: any) => node.data('textValign') || 'center',
          'text-halign': (node: any) => node.data('textHalign') || 'center',
          'text-margin-y': (node: any) => {
            const val = node.data('textMarginY');
            if (val === undefined || val === null) return DefaultValues.node.properties.textMarginY;
            return node.data('textValign') === 'top' ? val * -1 : val;
          },
          'text-margin-x': (node: any) => {
            const val = node.data('textMarginX');
            if (val === undefined || val === null) return DefaultValues.node.properties.textMarginX;
            return node.data('textHalign') === 'left' && val !== 0 ? val * -1 : val;
          },
          'text-wrap': 'wrap',
          'text-max-width': '100px'
        }
      },
      {
        selector: 'node:parent',
        style: {
          'shape': 'roundrectangle',
          'background-color': (node: any) => {
            const color = node.data('color') || DefaultValues.node.color;
            const nodeOpacity = node.data('opacity');

            if (nodeOpacity === 0 || nodeOpacity === '0') {
              return isDark ? '#000' : '#fff';
            }

            const trueOpacity = nodeOpacity ?? DefaultValues.node.properties.opacity;
            return transformColorWithHSL(color, targetLuminance, trueOpacity);
          },
          'background-image': (node: any) => {
            const imageUrl = node.data('imageUrl');
            return imageUrl
                ? environment.apiUrl + '/public/image?url=' + encodeURIComponent(imageUrl)
                : 'none';
          },
          'background-image-opacity': (node: any) => {
            const imageOpacity = node.data('imageOpacity');
            const opacity = imageOpacity ?? DefaultValues.node.properties.imageOpacity ?? 1;
            return opacity > 1 ? 1 : opacity;
          },
          'background-fit': 'cover',
          'z-index': 2,
          'z-index-compare': 'manual'
        }
      },
      {
        selector: 'edge',
        style: {
          'curve-style': (edge: any) => {
            const type = edge.data('type') || 'BEZIER';
            return (EdgeTypeMapCy as any)[type] || 'bezier';
          },
          'target-arrow-shape': (edge: any) => {
            const direction = edge.data('direction');
            const arrowType = edge.data('edgeArrowType') || DefaultValues.link.edgeArrowType;
            return (direction !== 'undirected' || direction === undefined) ? arrowType : 'none';
          },
          'source-arrow-shape': (edge: any) => {
            const direction = edge.data('direction');
            const arrowType = edge.data('edgeArrowType') || DefaultValues.link.edgeArrowType;
            return direction === 'bidirected' ? arrowType : 'none';
          },
          'line-color': (edge: any) => edge.data('color') || DefaultValues.link.color,
          'target-arrow-color': (edge: any) => edge.data('color') || DefaultValues.link.color,
          'source-arrow-color': (edge: any) => edge.data('color') || DefaultValues.link.color,
          'arrow-scale': (edge: any) => {
            const weight = edge.data('weight') as number;
            return weight >= 1 ? 0.5 + 0.3 * Math.log(weight) : 0.3;
          },
          'width': (edge: any) => previewLinkLineWidth(edge),
          'line-style': (edge: any) => cytoscapeStrokeStyle(previewLinkStyle(edge)),
          // MAP-346: see the node border above.
          //@ts-ignore
          'line-dash-pattern': (edge: any) =>
            strokeDashPattern(previewLinkStyle(edge), previewLinkLineWidth(edge)) ?? [6, 3],
          //@ts-ignore
          'line-cap': (edge: any) => {
            const style = previewLinkStyle(edge);
            const arrowType = edge.data('edgeArrowType') || DefaultValues.link.edgeArrowType;
            const headless = edge.data('direction') === 'undirected' || arrowType === 'none';
            return headless && style === 'solid' ? 'round' : strokeLineCap(style);
          },
          'target-endpoint': 'outside-to-node',
          'source-endpoint': 'outside-to-node'
        }
      },
      // Edge labels - only for edges with label property defined
      {
        selector: 'edge[label]',
        style: {
          'label': (edge: any) => {
            const label = edge.data('label');
            if (label === 'name') {
              return edge.data('name') || '';
            }
            // For preview, we don't load metadata, so just use name as fallback
            return edge.data('name') || '';
          },
          'font-family': 'Roboto, sans-serif',
          'font-size': (edge: any) => edge.data('textFontSize') ?? DefaultValues.link.textFontSize,
          'color': labelColor,
          'text-rotation': (edge: any) => {
            const labelSlope = edge.data('labelSlope') ?? DefaultValues.link.labelSlope;
            return labelSlope === 'sloped' ? 'autorotate' : 0;
          },
          'text-margin-y': (edge: any) => {
            const offset = edge.data('linkLabelOffset') ?? DefaultValues.link.linkLabelOffset;
            return -offset;
          }
        }
      }
    ];
  }
}
